import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Plus,
  Trash,
  Package,
  CaretDown,
  CaretRight,
  PencilSimple,
  Truck,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { photoSrc } from "@/components/AppHeader";
import { api, formatApiErrorDetail } from "@/lib/api";
import { shrinkForUpload } from "@/lib/shrinkImage";

const sf = (w, h) => ((Number(w) || 0) * (Number(h) || 0)) / 144;
const fmtSF = (n) => `${(Number(n) || 0).toFixed(3)} SF`;

const blankRow = (line) => ({
  barcode: "",
  note: "",
  lot: "",
  bundle: "",
  supplier_lot: "",
  bin: "",
  packing_width: line.length_in || 0,
  packing_height: line.width_in || 0,
  received_width: line.length_in || 0,
  received_height: line.width_in || 0,
  image: null,
});

const Cell = ({ editable, value, onChange, testid, type = "text", list, right }) =>
  editable ? (
    <Input
      list={list}
      type={type}
      data-testid={testid}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      className={`h-9 border-zinc-800 bg-zinc-950 text-xs ${right ? "text-right" : ""}`}
    />
  ) : (
    <span className="font-mono-data text-zinc-300">
      {value === "" || value == null ? "—" : value}
    </span>
  );

function RowGrid({
  rows,
  editable,
  onCell,
  onRemove,
  onImage,
  uploadingIdx,
  locations = [],
  testidPrefix,
}) {
  return (
    <div className="overflow-x-auto rounded-lg border border-zinc-800">
      <datalist id={`${testidPrefix}-bins`}>
        {locations.map((l) => (
          <option key={l} value={l} />
        ))}
      </datalist>
      <table
        className="w-full min-w-[1080px] text-left text-xs"
        data-testid={`${testidPrefix}-table`}
      >
        <thead>
          <tr className="border-b border-zinc-800 text-[10px] uppercase tracking-widest text-zinc-500">
            <th className="p-2">Barcode</th>
            <th className="p-2">Note</th>
            <th className="p-2">Lot</th>
            <th className="p-2">Bundle</th>
            <th className="p-2">Supplier Lot</th>
            <th className="p-2">Bin</th>
            <th className="p-2 text-right">Pack W</th>
            <th className="p-2 text-right">Pack H</th>
            <th className="p-2 text-right">Recv W</th>
            <th className="p-2 text-right">Recv H</th>
            <th className="p-2">Img</th>
            {editable && <th className="p-2" />}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr
              key={i}
              data-testid={`${testidPrefix}-row-${i}`}
              className="border-b border-zinc-800/60 last:border-0"
            >
              <td className="p-1.5">
                <Cell
                  editable={editable}
                  value={r.barcode}
                  onChange={(v) => onCell(i, "barcode", v)}
                  testid={`${testidPrefix}-row-${i}-barcode`}
                />
              </td>
              <td className="p-1.5">
                <Cell
                  editable={editable}
                  value={r.note}
                  onChange={(v) => onCell(i, "note", v)}
                  testid={`${testidPrefix}-row-${i}-note`}
                />
              </td>
              <td className="p-1.5">
                <Cell
                  editable={editable}
                  value={r.lot}
                  onChange={(v) => onCell(i, "lot", v)}
                  testid={`${testidPrefix}-row-${i}-lot`}
                />
              </td>
              <td className="p-1.5">
                <Cell
                  editable={editable}
                  value={r.bundle}
                  onChange={(v) => onCell(i, "bundle", v)}
                  testid={`${testidPrefix}-row-${i}-bundle`}
                />
              </td>
              <td className="p-1.5">
                <Cell
                  editable={editable}
                  value={r.supplier_lot}
                  onChange={(v) => onCell(i, "supplier_lot", v)}
                  testid={`${testidPrefix}-row-${i}-supplier_lot`}
                />
              </td>
              <td className="p-1.5">
                <Cell
                  editable={editable}
                  value={r.bin}
                  onChange={(v) => onCell(i, "bin", v)}
                  list={`${testidPrefix}-bins`}
                  testid={`${testidPrefix}-row-${i}-bin`}
                />
              </td>
              <td className="p-1.5">
                <Cell
                  editable={editable}
                  value={r.packing_width}
                  onChange={(v) => onCell(i, "packing_width", v)}
                  type="number"
                  right
                  testid={`${testidPrefix}-row-${i}-packing_width`}
                />
              </td>
              <td className="p-1.5">
                <Cell
                  editable={editable}
                  value={r.packing_height}
                  onChange={(v) => onCell(i, "packing_height", v)}
                  type="number"
                  right
                  testid={`${testidPrefix}-row-${i}-packing_height`}
                />
              </td>
              <td className="p-1.5">
                <Cell
                  editable={editable}
                  value={r.received_width}
                  onChange={(v) => onCell(i, "received_width", v)}
                  type="number"
                  right
                  testid={`${testidPrefix}-row-${i}-received_width`}
                />
              </td>
              <td className="p-1.5">
                <Cell
                  editable={editable}
                  value={r.received_height}
                  onChange={(v) => onCell(i, "received_height", v)}
                  type="number"
                  right
                  testid={`${testidPrefix}-row-${i}-received_height`}
                />
              </td>
              <td className="p-1.5">
                {editable ? (
                  <label className="inline-flex cursor-pointer items-center gap-1">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      data-testid={`${testidPrefix}-row-${i}-image-input`}
                      onChange={(e) => {
                        onImage(i, e.target.files?.[0]);
                        e.target.value = "";
                      }}
                    />
                    {r.image ? (
                      <img
                        src={photoSrc(r.image)}
                        alt=""
                        className="h-8 w-8 rounded object-cover"
                      />
                    ) : (
                      <span className="flex h-8 w-8 items-center justify-center rounded border border-zinc-700 text-zinc-400 hover:bg-zinc-800">
                        {uploadingIdx === i ? "…" : <Plus size={13} weight="bold" />}
                      </span>
                    )}
                  </label>
                ) : r.image ? (
                  <img
                    src={photoSrc(r.image)}
                    alt=""
                    className="h-8 w-8 rounded object-cover"
                  />
                ) : (
                  <span className="text-zinc-600">—</span>
                )}
              </td>
              {editable && (
                <td className="p-1.5 text-right">
                  <button
                    type="button"
                    data-testid={`${testidPrefix}-row-${i}-remove`}
                    onClick={() => onRemove(i)}
                    className="text-zinc-500 hover:text-red-400"
                  >
                    <Trash size={14} weight="bold" />
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const ShippingFields = ({ shipping, onChange, prefix }) => (
  <div className="grid gap-2 sm:grid-cols-4">
    {[
      ["carrier", "Carrier", "text"],
      ["tracking", "Tracking #", "text"],
      ["ship_date", "Ship date", "date"],
      ["cost", "Freight cost", "number"],
    ].map(([k, label, type]) => (
      <div key={k} className="space-y-1">
        <span className="text-[10px] uppercase tracking-widest text-zinc-500">
          {label}
        </span>
        <Input
          type={type}
          data-testid={`${prefix}-${k}`}
          value={shipping[k] ?? ""}
          onChange={(e) => onChange({ ...shipping, [k]: e.target.value })}
          className="h-9 border-zinc-800 bg-zinc-950 text-xs"
        />
      </div>
    ))}
  </div>
);

export function GoodsReceiptGrid({ po, line, locations = [], onCancel, onDone }) {
  const left = (line.qty || 0) - (line.received_qty || 0);
  const [rows, setRows] = useState(
    Array.from({ length: Math.max(1, left) }, () => blankRow(line)),
  );
  const [shipping, setShipping] = useState({
    carrier: "",
    tracking: "",
    ship_date: "",
    cost: "",
  });
  const [busy, setBusy] = useState(false);
  const [uploadingIdx, setUploadingIdx] = useState(null);
  const [paidCost, setPaidCost] = useState(
    line.unit_cost == null || line.unit_cost === "" ? "" : String(line.unit_cost),
  );

  const setCell = (i, k, v) =>
    setRows((rs) => rs.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const addRow = () => setRows((rs) => [...rs, blankRow(line)]);
  const removeRow = (i) => setRows((rs) => rs.filter((_, j) => j !== i));

  const uploadImage = async (i, file) => {
    if (!file) return;
    setUploadingIdx(i);
    const fd = new FormData();
    fd.append("file", await shrinkForUpload(file));
    try {
      const { data } = await api.post(
        `/purchase-orders/${po.id}/receipt-photo`,
        fd,
      );
      setCell(i, "image", data.image);
    } catch (e) {
      toast.error("Image upload failed");
    }
    setUploadingIdx(null);
  };

  const packedSF = rows.reduce((n, r) => n + sf(r.packing_width, r.packing_height), 0);
  const receivedSF = rows.reduce(
    (n, r) => n + sf(r.received_width, r.received_height),
    0,
  );

  const submit = async () => {
    if (!rows.length) {
      toast.error("Add at least one piece");
      return;
    }
    if (rows.length > left) {
      toast.error(`Only ${left} unit(s) left to receive`);
      return;
    }
    setBusy(true);
    try {
      await api.post(`/purchase-orders/${po.id}/receipts`, {
        line_id: line.line_id,
        paid_unit_cost: paidCost === "" ? null : Number(paidCost),
        shipping: { ...shipping, cost: Number(shipping.cost) || 0 },
        rows: rows.map((r) => ({
          ...r,
          packing_width: Number(r.packing_width) || 0,
          packing_height: Number(r.packing_height) || 0,
          received_width: Number(r.received_width) || 0,
          received_height: Number(r.received_height) || 0,
        })),
      });
      toast.success(`${rows.length} piece(s) received into stock`);
      onDone();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not receive pieces",
      );
    }
    setBusy(false);
  };

  return (
    <div className="space-y-3" data-testid="gr-grid">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs uppercase tracking-widest text-zinc-400">
          Goods receipt — {line.slab_color} {line.thickness} {line.finish}
        </div>
        <div className="font-mono-data text-xs text-zinc-500">
          <span data-testid="gr-packed-sf" className="text-zinc-400">
            {fmtSF(packedSF)} packed
          </span>{" "}
          ·{" "}
          <span data-testid="gr-received-sf" className="text-indigo-300">
            {fmtSF(receivedSF)} received
          </span>{" "}
          · {rows.length} unit(s), {left} left
        </div>
      </div>

      <div
        className="rounded-lg border border-zinc-800 bg-zinc-900/60 p-3"
        data-testid="gr-paid-cost-card"
      >
        <div className="flex flex-wrap items-end gap-x-4 gap-y-2">
          <div className="space-y-1">
            <span className="text-[10px] uppercase tracking-widest text-zinc-500">
              Paid price / ft²
            </span>
            <Input
              type="number"
              step="0.01"
              data-testid="gr-paid-cost"
              value={paidCost}
              onChange={(e) => setPaidCost(e.target.value)}
              className="h-9 w-32 border-zinc-800 bg-zinc-950 text-right font-mono-data text-xs"
            />
          </div>
          <div className="flex-1 text-[11px] leading-relaxed text-zinc-500">
            Ordered at{" "}
            <span className="font-mono-data text-zinc-400">
              {line.unit_cost == null || line.unit_cost === ""
                ? "—"
                : `$${Number(line.unit_cost).toFixed(2)}/ft²`}
            </span>
            .
            {paidCost !== "" &&
              line.unit_cost != null &&
              line.unit_cost !== "" &&
              Number(paidCost) !== Number(line.unit_cost) && (
                <span className="ml-1 font-semibold text-amber-300">
                  Updates this line&apos;s cost, the received slabs and supplier price
                  history.
                </span>
              )}
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-zinc-800 bg-zinc-900/60 p-3">
        <div className="mb-2 flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500">
          <Truck size={13} weight="bold" /> Shipping
        </div>
        <ShippingFields
          shipping={shipping}
          onChange={setShipping}
          prefix="gr-ship"
        />
      </div>

      <RowGrid
        rows={rows}
        editable
        onCell={setCell}
        onRemove={removeRow}
        onImage={uploadImage}
        uploadingIdx={uploadingIdx}
        locations={locations}
        testidPrefix="gr"
      />

      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          data-testid="gr-add-row"
          onClick={addRow}
          className="h-10 border-zinc-700 bg-transparent text-xs uppercase tracking-widest hover:bg-zinc-800"
        >
          <Plus size={14} weight="bold" className="mr-2" /> Add row
        </Button>
        <Button
          type="button"
          data-testid="gr-receive-all"
          disabled={busy}
          onClick={submit}
          className="h-10 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
        >
          <Package size={15} weight="bold" className="mr-2" />
          {busy ? "Receiving…" : `Receive ${rows.length} piece(s)`}
        </Button>
        <Button
          type="button"
          variant="outline"
          data-testid="gr-cancel"
          onClick={onCancel}
          className="h-10 border-zinc-700 bg-transparent text-xs uppercase tracking-widest hover:bg-zinc-800"
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}

export function GoodsReceiptHistory({ po, refreshKey, locations = [], onChange }) {
  const [receipts, setReceipts] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [editId, setEditId] = useState(null);
  const [editRows, setEditRows] = useState([]);
  const [editShip, setEditShip] = useState({});
  const [uploadingIdx, setUploadingIdx] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api
      .get(`/purchase-orders/${po.id}/receipts`)
      .then(({ data }) => setReceipts(data))
      .catch(() => setReceipts([]));
  }, [po.id]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const startEdit = (gr) => {
    setOpenId(gr.id);
    setEditId(gr.id);
    setEditRows((gr.rows || []).map((r) => ({ ...r })));
    setEditShip({ ...(gr.shipping || {}) });
  };

  const uploadImage = async (i, file) => {
    if (!file) return;
    setUploadingIdx(i);
    const fd = new FormData();
    fd.append("file", await shrinkForUpload(file));
    try {
      const { data } = await api.post(
        `/purchase-orders/${po.id}/receipt-photo`,
        fd,
      );
      setEditRows((rs) => rs.map((r, j) => (j === i ? { ...r, image: data.image } : r)));
    } catch (e) {
      toast.error("Image upload failed");
    }
    setUploadingIdx(null);
  };

  const saveEdit = async (gr) => {
    setBusy(true);
    try {
      await api.put(`/goods-receipts/${gr.id}`, {
        shipping: { ...editShip, cost: Number(editShip.cost) || 0 },
        rows: editRows.map((r) => ({
          ...r,
          packing_width: Number(r.packing_width) || 0,
          packing_height: Number(r.packing_height) || 0,
          received_width: Number(r.received_width) || 0,
          received_height: Number(r.received_height) || 0,
        })),
      });
      toast.success(`${gr.code} updated`);
      setEditId(null);
      load();
      onChange?.();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Could not save",
      );
    }
    setBusy(false);
  };

  const deleteGR = async (gr) => {
    setBusy(true);
    try {
      await api.delete(`/goods-receipts/${gr.id}`);
      toast.success(`${gr.code} removed — pieces pulled from stock`);
      setEditId(null);
      setOpenId(null);
      load();
      onChange?.();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not delete receipt",
      );
    }
    setBusy(false);
  };

  if (!receipts.length) return null;

  return (
    <div className="mt-8" data-testid="gr-history">
      <div className="text-xs uppercase tracking-widest text-zinc-500">
        Goods receipts ({receipts.length})
      </div>
      <div className="mt-3 space-y-3">
        {receipts.map((gr) => {
          const open = openId === gr.id;
          const editing = editId === gr.id;
          return (
            <div
              key={gr.id}
              data-testid={`gr-card-${gr.code}`}
              className="overflow-hidden rounded-xl border border-zinc-800 bg-zinc-900/60"
            >
              <div className="flex flex-wrap items-center justify-between gap-3 p-4">
                <button
                  type="button"
                  data-testid={`gr-expand-${gr.id}`}
                  onClick={() => setOpenId(open ? null : gr.id)}
                  className="flex min-w-0 items-center gap-2 text-left"
                >
                  {open ? (
                    <CaretDown size={16} weight="bold" className="text-zinc-500" />
                  ) : (
                    <CaretRight size={16} weight="bold" className="text-zinc-500" />
                  )}
                  <span>
                    <span className="font-mono-data text-xs text-indigo-300">
                      {gr.code}
                    </span>
                    <span className="ml-2 text-sm font-semibold text-zinc-100">
                      {gr.product_label}
                    </span>
                    <span className="mt-0.5 block text-[11px] text-zinc-500">
                      {gr.units} unit(s) · {fmtSF(gr.received_sqft)} received ·{" "}
                      {gr.created_by_name || gr.created_by} ·{" "}
                      {(gr.created_at || "").slice(0, 10)}
                    </span>
                  </span>
                </button>
                <div className="flex items-center gap-2">
                  {!editing ? (
                    <Button
                      type="button"
                      variant="outline"
                      data-testid={`gr-edit-${gr.id}`}
                      onClick={() => startEdit(gr)}
                      className="h-9 border-zinc-800 bg-zinc-900 text-[10px] uppercase tracking-widest hover:bg-zinc-800"
                    >
                      <PencilSimple size={13} weight="bold" className="mr-1" />{" "}
                      Bulk edit
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      data-testid={`gr-save-${gr.id}`}
                      disabled={busy}
                      onClick={() => saveEdit(gr)}
                      className="h-9 bg-indigo-500 text-[10px] font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
                    >
                      {busy ? "Saving…" : "Save"}
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    data-testid={`gr-delete-${gr.id}`}
                    disabled={busy}
                    onClick={() => deleteGR(gr)}
                    className="h-9 border-zinc-800 bg-zinc-900 text-[10px] uppercase tracking-widest text-red-400 hover:bg-zinc-800"
                  >
                    <Trash size={13} weight="bold" />
                  </Button>
                </div>
              </div>
              {open && (
                <div className="space-y-3 border-t border-zinc-800 bg-zinc-950/40 p-4">
                  {editing ? (
                    <div className="rounded-lg border border-zinc-800 bg-zinc-900/60 p-3">
                      <div className="mb-2 flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500">
                        <Truck size={13} weight="bold" /> Shipping
                      </div>
                      <ShippingFields
                        shipping={editShip}
                        onChange={setEditShip}
                        prefix={`gr-edit-ship-${gr.id}`}
                      />
                    </div>
                  ) : (
                    (gr.shipping?.carrier ||
                      gr.shipping?.tracking ||
                      gr.shipping?.cost) && (
                      <div className="font-mono-data text-xs text-zinc-500">
                        <Truck
                          size={12}
                          weight="bold"
                          className="mr-1 inline text-zinc-500"
                        />
                        {[
                          gr.shipping?.carrier,
                          gr.shipping?.tracking &&
                            `#${gr.shipping.tracking}`,
                          gr.shipping?.ship_date,
                          gr.shipping?.cost
                            ? `$${Number(gr.shipping.cost).toFixed(2)}`
                            : "",
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </div>
                    )
                  )}
                  <RowGrid
                    rows={editing ? editRows : gr.rows || []}
                    editable={editing}
                    onCell={(i, k, v) =>
                      setEditRows((rs) =>
                        rs.map((r, j) => (j === i ? { ...r, [k]: v } : r)),
                      )
                    }
                    onRemove={() => {}}
                    onImage={uploadImage}
                    uploadingIdx={uploadingIdx}
                    locations={locations}
                    testidPrefix={`gre-${gr.id}`}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
